import { useEffect, useState } from 'react';
import { UserX, Check } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import Dropdown from '../receptura/Dropdown';
import { TYP_CONFIG } from '../../pages/smeny/spolecne';

const AKCENT = '#10B981';
const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const pocetDni = (od, doo) => (od && doo && doo >= od
    ? Math.round((new Date(doo) - new Date(od)) / 86400000) + 1 : 0);

/**
 * „Upravit" u žádosti o nepřítomnost v notifikaci. Schvalovatel přepíše, co
 * nesedí (typicky hodiny), a jedním tlačítkem uloží i schválí — backend
 * (`POST /shifts/nedostupnosti/{id}/schvalit` s opravou v těle) to udělá
 * v jednom kroku, takže zaměstnanec dostane jedno upozornění se změnami.
 */
export default function UpravitNepritomnost({ nedostupnostId, isDark, onClose, onHotovo }) {
    const [form, setForm] = useState(null);
    const [puvodni, setPuvodni] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [ukladam, setUkladam] = useState(false);

    useEffect(() => {
        let alive = true;
        fetch(`/api/shifts/nedostupnosti/${nedostupnostId}`, { headers: { Accept: 'application/json' } })
            .then(async r => {
                const d = await r.json().catch(() => ({}));
                if (!r.ok) throw new Error(d.error || 'Žádost se nepodařilo načíst.');
                return d;
            })
            .then(d => {
                if (!alive) return;
                const f = { od: d.od, do: d.do, hodiny: d.hodiny ?? '', typ: d.typ, poznamka: d.poznamka || '' };
                setForm(f);
                setPuvodni({ ...d, ...f });
            })
            .catch(e => alive && setChyba(e.message));
        return () => { alive = false; };
    }, [nedostupnostId]);

    const hod = Number(String(form?.hodiny ?? '').replace(',', '.'));
    const dni = form ? pocetDni(form.od, form.do) : 0;
    const lze = !!form && !ukladam && dni > 0 && hod >= 0.5 && hod <= 24;

    const ulozitASchvalit = async () => {
        setUkladam(true);
        setChyba(null);
        try {
            const res = await fetch(`/api/shifts/nedostupnosti/${nedostupnostId}/schvalit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ ...form, hodiny: hod, poznamka: form.poznamka.trim() || null }),
            });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) {
                const prvni = d.errors ? Object.values(d.errors)[0]?.[0] : null;
                throw new Error(d.error || prvni || d.message || 'Nepodařilo se uložit.');
            }
            onHotovo?.();
        } catch (e) {
            setChyba(e.message);
        } finally {
            setUkladam(false);
        }
    };

    const hrana = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const pole = { width: '100%', height: 40, borderRadius: 8, padding: '0 11px', outline: 'none', fontSize: 14,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${hrana}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light' };
    const popisek = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
        color: 'var(--text-muted)', margin: '12px 0 5px', display: 'block' };
    const nastav = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const jmeno = puvodni?.zamestnanec ? `${puvodni.zamestnanec.jmeno} ${puvodni.zamestnanec.prijmeni}` : '';

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Upravit a schválit nepřítomnost" ikona={UserX}
            barva={AKCENT} sirka={440} zavritLze={!ukladam}
            paticka={(
                <div className="flex items-center gap-2">
                    {chyba && form && <span className="text-xs flex-1" style={{ color: '#EF4444' }}>{chyba}</span>}
                    <button type="button" onClick={onClose} disabled={ukladam} className="rounded-lg ml-auto"
                        style={{ height: 42, padding: '0 16px', background: 'transparent', color: 'var(--text-secondary)',
                            border: `1px solid ${hrana}`, cursor: 'pointer' }}>
                        Zrušit
                    </button>
                    <button type="button" onClick={ulozitASchvalit} disabled={!lze}
                        className="rounded-lg font-semibold inline-flex items-center gap-1.5"
                        style={{ height: 42, padding: '0 18px', background: AKCENT, color: '#fff', border: 'none',
                            cursor: lze ? 'pointer' : 'not-allowed', opacity: lze ? 1 : 0.45 }}>
                        <Check size={16} />{ukladam ? 'Ukládám…' : 'Uložit a schválit'}
                    </button>
                </div>
            )}>
            <div style={{ padding: '0 16px 16px' }}>
                {!form ? (
                    <p className="text-sm" style={{ marginTop: 14, color: chyba ? '#EF4444' : 'var(--text-muted)' }}>
                        {chyba || 'Načítám žádost…'}
                    </p>
                ) : (<>
                    {jmeno && (
                        <p style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{jmeno}</p>
                    )}

                    <span style={popisek}>Typ</span>
                    <Dropdown value={form.typ} onChange={nastav('typ')} isDark={isDark} accent={AKCENT} height={40}
                        options={Object.entries(TYP_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))} />

                    <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                            <span style={popisek}>Od</span>
                            <input type="date" value={form.od} style={pole}
                                onChange={e => setForm(f => ({ ...f, od: e.target.value, do: f.do < e.target.value ? e.target.value : f.do }))} />
                        </div>
                        <div>
                            <span style={popisek}>Do</span>
                            <input type="date" value={form.do} min={form.od} onChange={nastav('do')} style={pole} />
                        </div>
                    </div>

                    <span style={popisek}>Hodin za den</span>
                    <input type="number" inputMode="decimal" min="0.5" max="24" step="0.5" value={form.hodiny}
                        onChange={nastav('hodiny')} placeholder="např. 8" style={pole} />
                    {dni > 1 && hod > 0 && (
                        <span className="text-xs" style={{ display: 'block', marginTop: 4, color: 'var(--text-muted)' }}>
                            Celkem {(hod * dni).toLocaleString('cs-CZ')} h za {dni} {dni < 5 ? 'dny' : 'dní'}
                        </span>
                    )}

                    <span style={popisek}>Poznámka</span>
                    <input type="text" value={form.poznamka} onChange={nastav('poznamka')} placeholder="Volitelné"
                        maxLength={500} style={pole} />

                    <p className="text-xs" style={{ marginTop: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        Uložením žádost zároveň schválíš. Zaměstnanec dostane upozornění i s tím, co se změnilo.
                    </p>
                </>)}
            </div>
        </ModalShell>
    );
}
